import { useCallback, useMemo, useRef, useState } from "react";
import type { RectVm } from "../../../utils/viewModels.types";
import { defaultMatches, stepIndex } from "./PreviewAsPicker.mappers";
import type { PreviewAsPickerProps } from "./PreviewAsPicker.types";

type Options = Pick<PreviewAsPickerProps, "options" | "value" | "onChange" | "matches">;

/** Open state, the search, the keyboard's row, and prev and next. */
export function usePreviewAsPickerInternalState({ options, value, onChange, matches = defaultMatches }: Options) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [anchor, setAnchor] = useState<RectVm | null>(null);
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);

  const index = useMemo(() => options.findIndex((option) => option.id === value), [options, value]);
  const selected = index === -1 ? null : options[index];
  const filtered = useMemo(() => options.filter((option) => matches(option, query)), [options, query, matches]);

  const close = useCallback(() => {
    setAnchor(null);
    setQuery("");
    setActiveId(null);
  }, []);
  const open = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setAnchor({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    setActiveId(value);
  }, [value]);
  const choose = useCallback(
    (id: string) => {
      onChange(id);
      close();
    },
    [onChange, close],
  );
  const step = useCallback(
    (direction: 1 | -1) => {
      const next = options[stepIndex(index, direction, options.length)];
      if (next) onChange(next.id);
    },
    [index, options, onChange],
  );

  return { triggerRef, anchor, query, setQuery, activeId, setActiveId, index, selected, filtered, open, close, choose, step };
}
